// ============================================
// components/ToboHeader.js
//
// Outlet switching rules:
//   - If user logged in with admin_pin (isAdmin === true):
//       Switch freely — no PIN required (12h session already authenticated)
//   - Any other login method (password, outlet_pin, manager_pin):
//       Must enter admin PIN to switch outlets
// ============================================

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Bell, ChevronDown, Store, Menu, Check, Loader2,
  Lock, X, TrendingUp, TrendingDown, ShoppingBag, Calendar, ShieldCheck,
} from 'lucide-react';
import { useAuth } from '../../common/context/AuthContext';
import { API_URL } from '../../api/config';
import { toast } from 'react-toastify';

const ToboHeader = ({ onMenuClick }) => {
  const location = useLocation();
  const navigate  = useNavigate();
  const { userData, userToken: token, signIn: authSignIn } = useAuth();

  const [restaurantInfo, setRestaurantInfo] = useState({
    restaurantName: userData?.restaurantName || '',
    logoUrl: userData?.logoUrl || '',
  });

  const [showOutletDropdown, setShowOutletDropdown] = useState(false);
  const [availableOutlets, setAvailableOutlets]     = useState([]);
  const [loadingOutlets, setLoadingOutlets]         = useState(false);

  // Admin PIN modal
  const [showPinModal, setShowPinModal]                       = useState(false);
  const [selectedOutletForSwitch, setSelectedOutletForSwitch] = useState(null);
  const [pin, setPin]                 = useState(['', '', '', '']);
  const [verifyingPin, setVerifyingPin] = useState(false);
  const [pinError, setPinError]       = useState('');

  const dropdownRef = useRef(null);
  const pinRefs     = useRef([]);
  const notifRef    = useRef(null);

  // Notification panel
  const [showNotifPanel, setShowNotifPanel] = useState(false);
  const [notifications, setNotifications]   = useState([]);
  const [notifLoading, setNotifLoading]     = useState(false);
  const [unreadCount, setUnreadCount]       = useState(0);

  // ── Logo fetch ──────────────────────────────────────────
  useEffect(() => {
    const load = async () => {
      if (!userData?.restaurantName) return;
      setRestaurantInfo({ restaurantName: userData.restaurantName, logoUrl: userData.logoUrl || '' });
      if (!userData.logoUrl && token) {
        try {
          const res = await fetch(`${API_URL}/settings`, { headers: { Authorization: `Bearer ${token}` } });
          if (res.ok) {
            const d = await res.json();
            if (d.success && d.data?.logoUrl)
              setRestaurantInfo(p => ({ ...p, logoUrl: d.data.logoUrl }));
          }
        } catch {}
      }
    };
    load();
  }, [userData, token]);

  // ── Pre-fetch unread notification count on mount ────────
  useEffect(() => {
    const checkUnread = async () => {
      if (!token) return;
      try {
        const res = await fetch(`${API_URL}/push/history?limit=50`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.ok) {
          const d = await res.json();
          const count = (d.notifications || []).filter(n => !n.readAt).length;
          setUnreadCount(count);
        }
      } catch {}
    };
    checkUnread();
  }, [token]);

  // ── Close on outside click ──────────────────────────────
  useEffect(() => {
    const handler = e => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setShowOutletDropdown(false);
      if (notifRef.current && !notifRef.current.contains(e.target)) setShowNotifPanel(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  // ── Focus first PIN box when modal opens ───────────────
  useEffect(() => {
    if (showPinModal) {
      setPin(['', '', '', '']);
      setPinError('');
      setTimeout(() => pinRefs.current[0]?.focus(), 100);
    }
  }, [showPinModal]);

  // ── Page title ─────────────────────────────────────────
  const getTitle = () => {
    const map = {
      '/dashboard': '../pages/Dashboard', '/product': 'Menu', '/expense': 'Daily Expenses',
      '/new-bill': 'New Bill', '/kot': 'Kitchen View', '/table-manager': 'Table Activity',
      '/expense-report': 'Expense Reports', '/sales-report': 'Items Report',
      '/gst-report': 'GST Report', '/settings': '../settings/Settings',
      '/ViewSubscriptionInfo': 'Subscription Info', '/support': 'Support Center',
      '/view-support': 'View Support', '/hold-bill': 'Hold Bills',
      '/menu-selection': 'Menu Selection', '/bill-history': 'Bill History',
      '/peak-hours-analytics': 'Peak Hours Analytics', '/subscription': 'Subscription',
      '/daily-notification': 'Notification Settings', '/account-delete': 'Account Settings',
    };
    const p = location.pathname;
    if (p.startsWith('/billdetails/')) return 'Bill Details';
    if (p.startsWith('/print-bill/'))  return 'Print Bill';
    if (p.startsWith('/print-kot/'))   return 'Print KOT';
    return map[p] || 'TOBO Billing';
  };

  // ── Fetch available outlets ─────────────────────────────
  const fetchAvailableOutlets = async () => {
    if (!userData?.email) return;
    setLoadingOutlets(true);
    try {
      const res = await fetch(`${API_URL}/auth/available-outlets`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ email: userData.email }),
      });
      if (res.ok) {
        const d = await res.json();
        if (d.success && d.outlets?.length) setAvailableOutlets(d.outlets);
      }
    } catch (e) {
      console.error('Error fetching outlets:', e);
    } finally {
      setLoadingOutlets(false);
    }
  };

  const handleOutletDropdownToggle = () => {
    if (!showOutletDropdown && !availableOutlets.length) fetchAvailableOutlets();
    setShowOutletDropdown(v => !v);
  };

  // ── Outlet click — decide if PIN needed ─────────────────
  const handleOutletClick = (outlet) => {
    if (outlet.restaurantId === userData?.restaurantId) {
      setShowOutletDropdown(false);
      return;
    }

    // Admin session (logged in with admin_pin): switch freely
    if (userData?.loginMethod === 'admin_pin' || userData?.isAdmin === true) {
      setShowOutletDropdown(false);
      doOutletSwitch(outlet, true);
      return;
    }

    // All other sessions: require admin PIN
    setSelectedOutletForSwitch(outlet);
    setShowPinModal(true);
    setShowOutletDropdown(false);
  };

  // ── Switch outlet via /api/auth/switch-outlet ───────────
  const doOutletSwitch = async (outlet, isAdminSession = false, adminPin = null) => {
    try {
      const res = await fetch(`${API_URL}/auth/switch-outlet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          targetRestaurantId: outlet.restaurantId,
          adminPin: adminPin || undefined,
          isAdminSession,
        }),
      });

      const data = await res.json();
      if (data.success) {
        await authSignIn({ token: data.token, user: data.user });
        toast.success(`Switched to ${outlet.outletName || outlet.restaurantName}`);
        setTimeout(() => window.location.reload(), 500);
      } else {
        toast.error(data.message || 'Failed to switch outlet');
      }
    } catch {
      toast.error('Failed to switch outlet');
    }
  };

  // ── PIN input handlers ──────────────────────────────────
  const handlePinChange = (index, value) => {
    if (value.length > 1 || (value && !/^\d$/.test(value))) return;
    const next = [...pin];
    next[index] = value;
    setPin(next);
    setPinError('');
    if (value && index < 3) pinRefs.current[index + 1]?.focus();
    if (index === 3 && value) {
      const full = [...next.slice(0, 3), value].join('');
      handleVerifyAndSwitch(full);
    }
  };

  const handlePinKeyDown = (index, e) => {
    if (e.key === 'Backspace' && !pin[index] && index > 0)
      pinRefs.current[index - 1]?.focus();
  };

  // ── Verify admin PIN then switch ────────────────────────
  const handleVerifyAndSwitch = async (pinToVerify = null) => {
    const pinString = pinToVerify || pin.join('');
    if (pinString.length !== 4) { setPinError('Please enter complete 4-digit PIN'); return; }
    if (!selectedOutletForSwitch) return;

    setVerifyingPin(true);
    setPinError('');

    try {
      // Use /api/auth/switch-outlet — backend verifies admin PIN and issues new token
      const res = await fetch(`${API_URL}/auth/switch-outlet`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: userData.email,
          targetRestaurantId: selectedOutletForSwitch.restaurantId,
          adminPin: pinString,
          isAdminSession: false,
        }),
      });

      const data = await res.json();

      if (!res.ok || !data.success) {
        setPinError(data.message || 'Invalid admin PIN. Please try again.');
        setPin(['', '', '', '']);
        pinRefs.current[0]?.focus();
        return;
      }

      await authSignIn({ token: data.token, user: data.user });
      setShowPinModal(false);
      setSelectedOutletForSwitch(null);
      setPin(['', '', '', '']);
      toast.success(`Switched to ${selectedOutletForSwitch.outletName}`);
      setTimeout(() => window.location.reload(), 500);
    } catch {
      setPinError('Failed to switch outlet. Please try again.');
      setPin(['', '', '', '']);
      pinRefs.current[0]?.focus();
    } finally {
      setVerifyingPin(false);
    }
  };

  const handleCloseModal = () => {
    setShowPinModal(false);
    setSelectedOutletForSwitch(null);
    setPin(['', '', '', '']);
    setPinError('');
  };

  // ── Notifications ───────────────────────────────────────
  const fetchNotifications = useCallback(async () => {
    if (!token) return;
    setNotifLoading(true);
    try {
      const res = await fetch(`${API_URL}/push/history`, { headers: { Authorization: `Bearer ${token}` } });
      if (res.ok) {
        const d = await res.json();
        const items = d.notifications || [];
        setNotifications(items);
        setUnreadCount(items.filter(n => !n.readAt).length);
      }
    } catch {}
    finally { setNotifLoading(false); }
  }, [token]);

  const handleNotifications = () => {
    setShowNotifPanel(prev => {
      if (!prev) { fetchNotifications(); setTimeout(() => setUnreadCount(0), 1500); }
      return !prev;
    });
  };

  const formatNotifDate = (d) => new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });

  const parseNotifBody = (body = '') => {
    const r = {};
    body.split('\n').map(l => l.trim()).forEach(l => {
      if (l.startsWith('Outlet:'))       r.outlet  = l.replace('Outlet:', '').trim();
      if (l.startsWith('Total Sales:'))  r.sales   = l.replace('Total Sales:', '').trim();
      if (l.startsWith('Total Orders:')) r.orders  = l.replace('Total Orders:', '').trim();
      if (l.includes('Sales change') || l.includes('vs Last')) r.change = l;
    });
    return r;
  };

  // ── Is current user an admin session? ───────────────────
  const isAdminUser = userData?.loginMethod === 'admin_pin' || userData?.isAdmin === true;

  return (
    <>
      <header className="h-16 bg-white border-b border-slate-200 px-4 md:px-8 flex items-center justify-between sticky top-0 z-20 shrink-0">
        {/* Left */}
        <div className="flex items-center gap-3">
          {onMenuClick && (
            <button onClick={onMenuClick} className="lg:hidden p-2 hover:bg-slate-100 rounded-lg transition-colors">
              <Menu size={20} className="text-slate-600" />
            </button>
          )}
          <h2 className="text-lg md:text-xl font-bold text-slate-800 truncate">{getTitle()}</h2>
        </div>

        <div className="flex items-center gap-3 md:gap-6">
          {/* Switch Outlet */}
          <div className="relative" ref={dropdownRef}>
            <button
              onClick={handleOutletDropdownToggle}
              className="flex items-center gap-2 bg-white border border-slate-200 px-3 py-1.5 md:px-4 md:py-2 rounded-xl text-xs md:text-sm font-bold text-slate-700 shadow-sm hover:bg-slate-50 transition-all"
            >
              <Store size={16} className="text-slate-400" />
              <span className="hidden sm:inline max-w-[120px] truncate">
                {userData?.outletName || userData?.restaurantName || 'Switch Outlet'}
              </span>
              <span className="sm:hidden">Switch</span>
              {/* Show shield icon if admin session (free switching) */}
              {isAdminUser
                ? <ShieldCheck size={13} className="text-purple-500" />
                : <Lock size={13} className="text-slate-400" />
              }
              <ChevronDown size={14} className={`text-slate-400 transition-transform ${showOutletDropdown ? 'rotate-180' : ''}`} />
            </button>

            {showOutletDropdown && (
              <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-xl shadow-lg overflow-hidden z-30">
                <div className="p-3 bg-slate-50 border-b border-slate-200">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide">Available Outlets</p>
                  {!isAdminUser && (
                    <p className="text-xs text-slate-400 mt-0.5">Admin PIN required to switch</p>
                  )}
                  {isAdminUser && (
                    <p className="text-xs text-purple-600 mt-0.5 font-medium">Admin session — switch freely</p>
                  )}
                </div>
                <div className="max-h-96 overflow-y-auto">
                  {loadingOutlets ? (
                    <div className="p-6 flex items-center justify-center">
                      <Loader2 className="animate-spin text-slate-400" size={24} />
                      <span className="ml-2 text-sm text-slate-500">Loading outlets...</span>
                    </div>
                  ) : availableOutlets.length === 0 ? (
                    <div className="p-6 text-center text-sm text-slate-500">No other outlets available</div>
                  ) : (
                    <div className="py-2">
                      {availableOutlets.map(outlet => (
                        <button
                          key={outlet.restaurantId}
                          onClick={() => handleOutletClick(outlet)}
                          disabled={outlet.restaurantId === userData?.restaurantId}
                          className="w-full px-4 py-3 flex items-center justify-between hover:bg-slate-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center flex-shrink-0">
                              <Store size={18} className="text-white" />
                            </div>
                            <div className="text-left">
                              <p className="text-sm font-bold text-slate-800">{outlet.restaurantName}</p>
                              <p className="text-xs text-slate-500">{outlet.outletName}</p>
                            </div>
                          </div>
                          {outlet.restaurantId === userData?.restaurantId
                            ? <div className="flex items-center gap-1 text-green-600"><Check size={16} /><span className="text-xs font-bold">Active</span></div>
                            : isAdminUser
                              ? <ShieldCheck size={14} className="text-purple-400" />
                              : <Lock size={14} className="text-slate-400" />
                          }
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Notifications + Profile */}
          <div className="flex items-center gap-2 md:gap-4">
            <div className="relative" ref={notifRef}>
              <button
                onClick={handleNotifications}
                className="p-2 md:p-2.5 bg-slate-50 border border-slate-200 rounded-full text-slate-500 hover:bg-slate-100 transition-colors relative"
              >
                <Bell size={18} />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-red-500 border-2 border-white rounded-full text-white text-[10px] font-bold flex items-center justify-center px-0.5">
                    {unreadCount > 9 ? '9+' : unreadCount}
                  </span>
                )}
              </button>

              {showNotifPanel && (
                <div className="absolute right-0 mt-2 w-80 bg-white border border-slate-200 rounded-2xl shadow-xl overflow-hidden z-30">
                  <div className="px-4 py-3 bg-slate-50 border-b border-slate-200 flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Bell size={14} className="text-slate-500" />
                      <p className="text-xs font-bold text-slate-700 uppercase tracking-wide">Sales Notifications</p>
                    </div>
                    <button onClick={() => setShowNotifPanel(false)} className="text-slate-400 hover:text-slate-600"><X size={14} /></button>
                  </div>
                  <div className="max-h-96 overflow-y-auto">
                    {notifLoading ? (
                      <div className="p-8 flex flex-col items-center gap-2">
                        <Loader2 className="animate-spin text-slate-400" size={20} />
                        <p className="text-xs text-slate-400">Loading...</p>
                      </div>
                    ) : notifications.length === 0 ? (
                      <div className="p-6 text-center text-sm text-slate-500">No notifications found</div>
                    ) : (
                      <div className="py-1">
                        {notifications.map((notif, idx) => {
                          const fields = parseNotifBody(notif.body);
                          const isPos  = notif.body?.includes('+');
                          return (
                            <div key={notif._id || idx} className={`px-4 py-3 border-b border-slate-100 last:border-0 hover:bg-slate-50 ${!notif.readAt ? 'bg-blue-50/40' : ''}`}>
                              <div className="flex items-start justify-between gap-2 mb-2">
                                <p className="text-xs font-bold text-slate-800 leading-tight">{notif.title || '📊 Daily Sales Update'}</p>
                                {!notif.readAt && <span className="w-2 h-2 bg-blue-500 rounded-full shrink-0 mt-1" />}
                              </div>
                              <div className="bg-slate-900 rounded-lg p-2.5 font-mono text-xs space-y-1">
                                {fields.outlet  && <p className="text-slate-300 flex items-center gap-1.5"><Store size={10} className="text-slate-500" /> {fields.outlet}</p>}
                                {fields.sales   && <p className="text-slate-300 flex items-center gap-1.5"><ShoppingBag size={10} className="text-slate-500" /> Sales: <span className="text-green-400 font-bold">{fields.sales}</span></p>}
                                {fields.orders  && <p className="text-slate-300 flex items-center gap-1.5"><Calendar size={10} className="text-slate-500" /> Orders: {fields.orders}</p>}
                                {fields.change  && <p className={`flex items-center gap-1.5 font-semibold ${isPos ? 'text-green-400' : 'text-red-400'}`}>{isPos ? <TrendingUp size={10} /> : <TrendingDown size={10} />} {fields.change}</p>}
                                <p className="text-slate-600 text-[9px] pt-0.5 border-t border-slate-700">— powered by TOBO</p>
                              </div>
                              {notif.sentAt && <p className="text-[10px] text-slate-400 mt-1.5">{formatNotifDate(notif.sentAt)}</p>}
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                  <div className="px-4 py-2.5 bg-slate-50 border-t border-slate-200">
                    <button onClick={() => setShowNotifPanel(false)} className="text-xs text-blue-600 hover:text-blue-700 font-semibold w-full text-center">
                      Manage notification settings →
                    </button>
                  </div>
                </div>
              )}
            </div>

            <div className="hidden lg:flex items-center gap-3 px-4 py-2 bg-slate-50 rounded-xl border border-slate-100">
              {restaurantInfo.logoUrl ? (
                <img src={restaurantInfo.logoUrl} alt={restaurantInfo.restaurantName} className="w-8 h-8 rounded-lg object-cover border border-slate-200" />
              ) : (
                <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                  <Store size={16} className="text-white" />
                </div>
              )}
              <div className="flex flex-col">
                <span className="text-xs font-black text-slate-400 uppercase tracking-widest">
                  {isAdminUser ? 'Admin' : 'Outlet'}
                </span>
                <span className="text-sm font-bold text-slate-800 truncate max-w-[150px]">
                  {restaurantInfo.restaurantName || 'Loading...'}
                </span>
              </div>
            </div>
          </div>
        </div>
      </header>

      {/* Admin PIN Modal */}
      {showPinModal && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="bg-gradient-to-br from-purple-600 to-purple-700 p-6 text-white relative">
              <button onClick={handleCloseModal} disabled={verifyingPin}
                className="absolute top-4 right-4 p-1 hover:bg-white/20 rounded-lg transition-colors disabled:opacity-50">
                <X size={20} />
              </button>
              <div className="w-14 h-14 bg-white/20 rounded-xl flex items-center justify-center mb-3">
                <ShieldCheck size={24} />
              </div>
              <h3 className="text-xl font-bold mb-1">Admin PIN Required</h3>
              <p className="text-purple-100 text-sm">Only the admin PIN can switch outlets</p>
            </div>

            <div className="p-6">
              {selectedOutletForSwitch && (
                <div className="mb-6 p-4 bg-slate-50 rounded-xl border border-slate-200">
                  <p className="text-xs font-bold text-slate-500 uppercase mb-2">Switching to</p>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center">
                      <Store size={18} className="text-white" />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-800">{selectedOutletForSwitch.restaurantName}</p>
                      <p className="text-xs text-slate-500">{selectedOutletForSwitch.outletName}</p>
                    </div>
                  </div>
                </div>
              )}

              <div className="mb-6">
                <label className="block text-xs font-bold text-slate-700 uppercase tracking-wider mb-3 text-center">
                  Enter Admin PIN
                </label>
                <div className="flex gap-3 justify-center">
                  {pin.map((digit, i) => (
                    <input
                      key={i}
                      ref={el => pinRefs.current[i] = el}
                      type="password"
                      maxLength={1}
                      value={digit}
                      onChange={e => handlePinChange(i, e.target.value)}
                      onKeyDown={e => handlePinKeyDown(i, e)}
                      disabled={verifyingPin}
                      className={`w-14 h-16 text-center text-2xl font-bold border-2 rounded-xl outline-none transition-all ${
                        pinError
                          ? 'border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-100'
                          : 'border-slate-300 focus:border-purple-500 focus:ring-4 focus:ring-purple-100'
                      } ${verifyingPin ? 'opacity-50 cursor-not-allowed' : ''}`}
                    />
                  ))}
                </div>
                {pinError && <p className="text-xs text-red-600 font-semibold text-center mt-3">{pinError}</p>}
              </div>

              <div className="flex gap-3">
                <button onClick={handleCloseModal} disabled={verifyingPin}
                  className="flex-1 py-3 px-4 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold rounded-xl transition-all disabled:opacity-50">
                  Cancel
                </button>
                <button
                  onClick={() => handleVerifyAndSwitch()}
                  disabled={pin.join('').length !== 4 || verifyingPin}
                  className="flex-1 py-3 px-4 bg-purple-600 hover:bg-purple-700 disabled:bg-slate-300 text-white font-bold rounded-xl transition-all flex items-center justify-center gap-2 disabled:cursor-not-allowed"
                >
                  {verifyingPin ? <><Loader2 className="animate-spin" size={18} /> Switching...</> : 'Switch Outlet'}
                </button>
              </div>
            </div>

            <div className="px-6 pb-6">
              <div className="bg-purple-50 border border-purple-100 rounded-xl p-3">
                <p className="text-xs text-purple-900 font-medium leading-relaxed">
                  🔒 The Admin PIN is one shared PIN for all outlets. To switch outlets freely, sign in with your Admin PIN instead.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ToboHeader;